import React from 'react';
import { Collapse, Grid, styled } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Colors, PrimaryButton, Text } from 'library';

interface FormSectionProps {
    title: string;
    subtitle?: string;
    icon: React.ReactElement;
    expanded: boolean;
    complete: boolean;
    onToggle: () => void;
    onNext?: () => void;
    autoFocusId?: string;
    children: React.ReactNode;
}

export default function FormSection({
    title,
    subtitle,
    icon,
    expanded,
    complete,
    onToggle,
    onNext,
    autoFocusId,
    children,
}: FormSectionProps) {
    return (
        <SectionCard complete={complete} expanded={expanded}>
            <AccentBar complete={complete} />
            <SectionContent>
                <Header onClick={onToggle}>
                    <Grid container alignItems="center" spacing={1} flexWrap="nowrap">
                        <Grid item>
                            {React.cloneElement(icon, {
                                sx: { color: complete ? Colors.turquoise : Colors.mediumGrey, fontSize: 20 },
                            })}
                        </Grid>
                        <Grid item xs>
                            <Text variant="body1" bold>
                                {title}
                            </Text>
                        </Grid>
                        <Grid item>
                            {complete ? (
                                <CheckCircleIcon sx={{ color: Colors.turquoise, fontSize: 22 }} />
                            ) : (
                                <ExpandIcon expanded={expanded} />
                            )}
                        </Grid>
                    </Grid>
                    {subtitle && !expanded && !complete && (
                        <Text variant="caption" color={Colors.mediumGrey} textStyle={{ marginTop: 2, marginLeft: 28 }}>
                            {subtitle}
                        </Text>
                    )}
                    {complete && !expanded && (
                        <Text variant="caption" color={Colors.turquoise} textStyle={{ marginTop: 2, marginLeft: 28 }}>
                            Completed
                        </Text>
                    )}
                </Header>

                <Collapse
                    in={expanded}
                    timeout={250}
                    onEntered={() => {
                        if (autoFocusId) {
                            document.getElementById(autoFocusId)?.focus();
                        }
                    }}
                >
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            onNext && onNext();
                        }}
                    >
                        {subtitle && (
                            <Text variant="caption" color={Colors.mediumGrey} textStyle={{ marginTop: 4, marginLeft: 28 }}>
                                {subtitle}
                            </Text>
                        )}
                        <FieldsContainer container spacing={2}>
                            {children}
                        </FieldsContainer>
                        {onNext && (
                            <NextButtonRow>
                                <PrimaryButton id={`${title}-next`} type="submit" disabled={!complete}>
                                    Next
                                </PrimaryButton>
                            </NextButtonRow>
                        )}
                    </form>
                </Collapse>
            </SectionContent>
        </SectionCard>
    );
}

const SectionCard = styled('div', {
    shouldForwardProp: (prop) => prop !== 'complete' && prop !== 'expanded',
})<{ complete: boolean; expanded: boolean }>(({ complete, expanded }) => ({
    backgroundColor: Colors.white,
    borderRadius: 12,
    border: `1px solid ${complete && !expanded ? Colors.mediumTurq : Colors.lightGrey}`,
    marginBottom: 12,
    overflow: 'hidden',
    boxShadow: expanded ? '0 2px 8px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.06)',
    display: 'flex',
    flexDirection: 'row',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
}));

const AccentBar = styled('div', {
    shouldForwardProp: (prop) => prop !== 'complete',
})<{ complete: boolean }>(({ complete }) => ({
    width: 4,
    backgroundColor: complete ? Colors.turquoise : Colors.fill,
    flexShrink: 0,
    transition: 'background-color 0.2s ease',
}));

const SectionContent = styled('div')({
    flex: 1,
    padding: '16px 20px',
    minWidth: 0,
});

const Header = styled('div')({
    cursor: 'pointer',
    userSelect: 'none',
});

const ExpandIcon = styled(ExpandMoreIcon, {
    shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ expanded }) => ({
    color: Colors.mediumGrey,
    fontSize: 22,
    transition: 'transform 0.25s ease',
    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
}));

const FieldsContainer = styled(Grid)({
    marginTop: 12,
});

const NextButtonRow = styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 16,
    maxWidth: 120,
    marginLeft: 'auto',
});
